import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, X, ArrowRight } from "lucide-react";
import { ASSETS, COMPANY } from "@/lib/site";

const photos = [
  { src: ASSETS.photos.bigHome, caption: "Manicured front lawn — full-property mow & edge" },
  { src: ASSETS.photos.mowLines, caption: "Crisp mow lines along the curb" },
  { src: ASSETS.photos.backyard, caption: "Backyard cleanup & maintenance" },
  { src: ASSETS.photos.fencedYard, caption: "Fenced backyard — even, well-kept turf" },
  { src: ASSETS.photos.sideYardStripes, caption: "Side yard with clean mow stripes" },
  { src: ASSETS.photos.curbsideMow, caption: "Curbside front lawn — sharp & professional" },
  { src: ASSETS.stock.mower, caption: "Sharp blades, clean cuts every visit" },
  { src: ASSETS.stock.blower, caption: "Driveways & walkways blown clean" },
  { src: ASSETS.stock.weedEater, caption: "Detailed weed eating around edges" },
];

const Gallery = () => {
  const [active, setActive] = useState(null);

  return (
    <div data-testid="gallery-page" className="bg-white">
      <section
        className="relative pt-32 pb-16 md:pt-40 md:pb-20"
        data-testid="gallery-hero"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${ASSETS.photos.bigHome})` }}
        />
        <div className="absolute inset-0 bg-[#0E3A1F]/80" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <span className="text-xs uppercase tracking-widest font-bold text-[#5BB23B]">
            Gallery
          </span>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">
            Lawns we’ve loved on.
          </h1>
          <p className="mt-4 text-white/85 text-lg max-w-2xl">
            A look at recent jobs across {COMPANY.serviceAreaShort}. Every yard
            gets the same hands-on care — yours could be next.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
            {photos.map((p, i) => (
              <button
                type="button"
                key={p.src}
                data-testid={`gallery-item-${i}`}
                onClick={() => setActive(p)}
                className="mb-5 block w-full overflow-hidden rounded-2xl group relative cursor-zoom-in break-inside-avoid"
              >
                <img
                  src={p.src}
                  alt={p.caption}
                  className="w-full object-cover hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/70 to-transparent text-left">
                  <span className="text-white text-sm font-medium">
                    {p.caption}
                  </span>
                </div>
              </button>
            ))}
          </div>

          <div className="mt-16 rounded-3xl bg-[#F5F5F0] border border-[#D4D4CE] p-8 md:p-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-[#0A0A0A]">
                Want yours featured next?
              </h3>
              <p className="mt-2 text-[#4B5563]">
                Request a free quote and let us get to work.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={COMPANY.telHref}
                data-testid="gallery-call-link"
                className="inline-flex items-center gap-2 sg-btn-dark rounded-full px-6 py-3.5 font-semibold"
              >
                <Phone className="h-4 w-4" /> {COMPANY.phoneDisplay}
              </a>
              <Link
                to="/quote"
                data-testid="gallery-quote-button"
                className="sg-btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3.5 font-semibold"
              >
                Free Quote <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {active && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setActive(null)}
        >
          <button
            type="button"
            aria-label="Close"
            data-testid="gallery-lightbox-close"
            className="absolute top-5 right-5 text-white/90 hover:text-white"
            onClick={() => setActive(null)}
          >
            <X className="h-7 w-7" />
          </button>
          <div
            className="max-w-5xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={active.src}
              alt={active.caption}
              className="w-full max-h-[80vh] object-contain rounded-xl"
            />
            <div className="mt-3 text-center text-white/80 text-sm">
              {active.caption}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
